import type { ApolloError } from '@apollo/client/errors'

import type {
  CheckBonusCodeForPlaySlipQueryVariables,
  LotteryId,
  PlaySlipPriceBreakdownInput,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import { QUERY_COUPON } from '~/constants/queryParams'

import { EDITOR_TICKET_TYPE } from '../constants/options'
import { useEditorOptionsStore } from '../stores/options'
import { useEditorPrice } from './editorPrice'

export const EDITOR_PROMO_CODE_TOAST_ID = 'editor-promo-code'

// shared with the checkout, so neither changes the code while the other works with it
export const useEditorPromoCodeLoading = (lotteryId: LotteryId) => {
  const isChecking = useState(`editorPromoCodeChecking-${lotteryId}`, () => false)
  const isRemoving = useState(`editorPromoCodeRemoving-${lotteryId}`, () => false)
  const isStoring = useState(`editorPromoCodeStoring-${lotteryId}`, () => false)
  const isLoading = computed(() => isChecking.value || isRemoving.value || isStoring.value)
  return { isChecking, isLoading, isRemoving, isStoring }
}

const REMOVAL_TOPIC_KEY = {
  system: 'bonus_code_removed_by_system',
  user: 'bonus_code_removed_by_player',
} as const

export const useEditorPromoCode = (lotteryId: LotteryId) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const route = useRoute()
  const { isLoggedIn } = useAuth()
  const { log } = useDatadog()
  const { n, t } = useI18n()
  const toaster = useToaster()

  const optionsStore = useEditorOptionsStore(lotteryId)
  const isSubscription = computed(() => optionsStore.ticketType !== EDITOR_TICKET_TYPE.Single)

  const { currentPrice } = useEditorPrice(lotteryId)

  const { coupon, couponSeen, productId } = useBonusCodeCondition(currentPrice)
  const { storeCoupon, storedCouponCode } = useStoredBonusCode()

  const { removeQueryParams } = useRouterUtils()

  const { load, refetch } = useCheckBonusCodeForPlaySlipLazyQuery()
  const { mutate } = useRemoveBonusCodeMutation()

  const checkedSignature = ref<Maybe<string>>(null)
  const errorMessage = ref<Maybe<string>>(null)
  const { isChecking, isLoading, isRemoving } = useEditorPromoCodeLoading(lotteryId)

  const expiryDelayMs = ref(0)
  const expiredHook = createEventHook()
  const visibility = useDocumentVisibility()
  const focused = useWindowFocus()
  // touch browsers (iOS) only fire focus on interaction and have no side by side windows
  const canHover = useMediaQuery('(hover: hover)')

  const verdict = computed(() => (coupon.value ? getBonusCodeVerdict(coupon.value) : null))

  const conditionMessage = computed(() => {
    const activeCoupon = coupon.value
    if (!activeCoupon) return null

    const [condition] = activeCoupon.unfulfilledConditions
    return toConditionMessage(activeCoupon, condition as Maybe<BonusCodeCondition>)
  })

  const toConditionMessage = (
    activeCoupon: NonNullable<BonusCodeCoupon>,
    condition: Maybe<BonusCodeCondition>,
  ) => {
    if (!condition) return null

    let amount: number | string | undefined

    switch (condition) {
      case BonusCodeCondition.MaxPlays:
      case BonusCodeCondition.MinPlays:
        amount = activeCoupon.info.value.amount
        break
      case BonusCodeCondition.MinTotalCharge:
        if (!currentPrice.value || !activeCoupon.minTotalCharge) break

        amount = n(evaluateBonusCode(activeCoupon, currentPrice.value).missingAmount / 100, {
          currency: 'USD',
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
          style: 'currency',
        })
        break
      case BonusCodeCondition.Subscription:
        if (!activeCoupon.fulfillable) {
          return t('bonuscodeinput.toast.validation.bonusconditions.subscription.unfulfillable')
        }
        break
    }

    return t(`bonuscodeinput.toast.${condition}`, { amount })
  }

  const toVariables = (bonusCode: string): CheckBonusCodeForPlaySlipQueryVariables => {
    const { groupedPlays, ...priceBreakdown } =
      currentPrice.value as PlaySlipPriceBreakdownInput & {
        groupedPlays?: unknown
      }

    return {
      input: {
        bonusCode,
        licensedTerritory,
        lotteryId,
        priceBreakdown,
        subscription: isSubscription.value,
      },
    }
  }

  const apply = async (bonusCode: string, userInitiated = false) => {
    if (!currentPrice.value || isLoading.value) return

    // the play slip may change while the check is in flight, so only its state at request time counts as checked
    const checkedPrice = currentPrice.value
    const checkedSubscription = isSubscription.value

    try {
      isChecking.value = true
      errorMessage.value = null

      const payload = await loadOrRefetch(load, refetch, toVariables(bonusCode))
      if (!payload) return

      handleResult(
        { ...payload.checkBonusCodeForPlaySlip, productId: lotteryId.toLowerCase() },
        userInitiated,
      )
      checkedSignature.value = toSignature(checkedPrice, checkedSubscription)
    } catch (error) {
      handleError(error as ApolloError, bonusCode, userInitiated)
    } finally {
      isChecking.value = false
    }
  }

  const handleError = (error: ApolloError, bonusCode: string, userInitiated: boolean) => {
    const [firstError] = error.graphQLErrors ?? []

    log('error', {
      component: 'editorPromoCode.ts',
      error,
      message: firstError?.message ?? error.message,
      messageContext: { lotteryId },
    })

    reject(
      bonusCode,
      userInitiated,
      firstError
        ? t(`bonuscodeinput.toast.error.${firstError.message}`)
        : t('bonuscodeinput.toast.error.internal.error'),
    )
  }

  const handleResult = (result: NonNullable<BonusCodeCoupon>, userInitiated: boolean) => {
    if (getBonusCodeVerdict(result) === BonusCodeVerdict.Unfulfillable) {
      reject(
        result.info.code,
        userInitiated,
        toConditionMessage(result, result.unfulfilledConditions[0] as BonusCodeCondition) ??
          t('bonuscodeinput.toast.error.validation.bonusconditions.unfulfillable'),
      )
      return
    }

    coupon.value = result
    announce(toastOptions())
  }

  const reject = (bonusCode: string, userInitiated: boolean, message: string) => {
    coupon.value = null

    if (!userInitiated) {
      announce(removalToast(bonusCode, 'system'))
      return
    }

    errorMessage.value = message
    announce(rejectedToast())
  }

  const remove = () => removeAs('user')

  const removeAs = async (actor: 'system' | 'user') => {
    const bonusCode = coupon.value?.info.code
    if (!bonusCode || isLoading.value) return

    try {
      if (isLoggedIn.value && productId.value) {
        isRemoving.value = true
        await mutate({ bonusCode, productId: productId.value })
      }
    } catch (error) {
      handleError(error as ApolloError, bonusCode, actor === 'user')
      return
    } finally {
      isRemoving.value = false
    }

    coupon.value = null
    checkedSignature.value = null
    errorMessage.value = null
    announce(removalToast(bonusCode, actor))
  }

  const takeCodeFromQuery = async () => {
    const queryCode = route.query[QUERY_COUPON]
    if (!queryCode) return false

    storeCoupon(String(queryCode))
    checkedSignature.value = recheckSignature.value

    try {
      await removeQueryParams(QUERY_COUPON)
    } catch {
      // a failed navigation must not keep the code from being applied
    }

    return true
  }

  const applyStoredCode = async (userInitiated: boolean) => {
    const storedCode = coupon.value?.info.code ?? storedCouponCode.value
    if (!storedCode || !currentPrice.value) return

    await apply(storedCode, userInitiated)
  }

  const removalToast = (bonusCode: string, actor: 'system' | 'user'): ToastOptions => ({
    headline: t(`bonuscodeinput.toast.remove.${actor}.headline`),
    message: t(`bonuscodeinput.toast.remove.${actor}.text`, { code: bonusCode }),
    tracking: { action: 'use_bonus_code', topicKey: REMOVAL_TOPIC_KEY[actor] },
    type: 'warning',
  })

  const announce = (options: Maybe<ToastOptions>) => {
    toaster.removeToast(EDITOR_PROMO_CODE_TOAST_ID)
    if (!options) return

    toaster.addToast(options, EDITOR_PROMO_CODE_TOAST_ID)
  }

  const rejectedToast = (): ToastOptions => ({
    headline: t('bonuscodeinput.toast.error.generic.headline'),
    message: errorMessage.value ?? '',
    tracking: { action: 'use_bonus_code', topicKey: 'bonus_code_conditions_unfulfillable' },
    type: 'danger',
  })

  const toastOptions = (): Maybe<ToastOptions> => {
    switch (verdict.value) {
      case BonusCodeVerdict.Fulfilled:
        if (couponSeen.value) return null
        couponSeen.value = true

        return {
          headline: t('bonuscodeinput.toast.apply.fulfilled.headline'),
          message: t('bonuscodeinput.toast.apply.fulfilled.text'),
          tracking: { action: 'use_bonus_code', topicKey: 'bonus_code_conditions_fulfilled' },
          type: 'success',
        }
      case BonusCodeVerdict.Fulfillable:
        couponSeen.value = false

        return {
          headline: t('bonuscodeinput.toast.apply.unfulfilled.headline'),
          message: conditionMessage.value ?? '',
          tracking: { action: 'use_bonus_code', topicKey: 'bonus_code_conditions_unfulfilled' },
          type: 'warning',
        }
      default:
        return null
    }
  }

  const toSignature = (price: typeof currentPrice.value, subscription: boolean) => {
    const activeCode = coupon.value?.info.code ?? storedCouponCode.value
    if (!activeCode || !price) return null

    const { minTotalChargeFulfilled } = evaluateBonusCode(coupon.value, price)
    const hasPlays = price.numberOfPlays > 0

    return `${activeCode}|${hasPlays}|${minTotalChargeFulfilled}|${subscription}`
  }

  const recheckSignature = computed(() => toSignature(currentPrice.value, isSubscription.value))

  // a change landing while a check or removal is in flight is picked up once it settles
  watch([recheckSignature, isLoading], ([signature, loading]) => {
    if (!signature || signature === checkedSignature.value || loading) return

    const activeCode = coupon.value?.info.code ?? storedCouponCode.value
    if (!activeCode) return

    checkedSignature.value = signature
    apply(activeCode)
  })

  const expire = async () => {
    const expiredCode = coupon.value?.info.code

    await until(
      () =>
        visibility.value === 'visible' && (!canHover.value || focused.value || document.hasFocus()),
    ).toBe(true)
    // a check or removal in flight would swallow the removal, wait for it to settle
    await until(isLoading).toBe(false)
    if (!expiredCode || coupon.value?.info.code !== expiredCode) return

    await removeAs('system')
    await expiredHook.trigger()
  }

  const { start: startExpiry, stop: stopExpiry } = useTimeoutFn(expire, expiryDelayMs, {
    immediate: false,
  })

  watch(
    () => coupon.value?.info.endDate,
    (endDate) => {
      stopExpiry()

      const delayMs = endDate ? getExpiryDelayMs(endDate, Date.now()) : null
      if (delayMs === null) return

      expiryDelayMs.value = delayMs
      startExpiry()
    },
    { immediate: true },
  )

  onMounted(async () => {
    couponSeen.value = false

    const fromQuery = await takeCodeFromQuery()
    await applyStoredCode(fromQuery)
  })

  return {
    apply,
    conditionMessage,
    coupon,
    errorMessage,
    isLoading,
    onExpired: expiredHook.on,
    remove,
    verdict,
  }
}
